import {ShopifyProductInfo} from '../shopify-product_info';
import {SphereItem} from '../../../sphere_item';
import {Planogram} from '../../../planogram';
import {ShopifyProductInfoService} from '../../../api/services/shopify-product_info.service';
import {ShoppingCartService} from '../../../api/services/shopping-cart.service';
import {CurrencyService} from '../../../api/services/currency.service';
import {VariantsComponent} from '../components/variants/variants_component';
import {QuantityPicker} from '../components/quantity-picker/quantity-picker';
import {ShopifyButtons} from '../components/buttons/shopify-buttons_component';
import {ProductFields} from '../components/product-fields/product-fields_component';
import {ShoppingCartWidgetComponent} from '../../../shopping-cart-widget/shopping-cart-widget.component';
import {shopifyHarryPotterOverlayView} from './shopify-harry-potter-product_info.view';
import {CurrencyUtils} from '../../../utils/currency_utils';
import {InputFieldsComponent} from './input-fields/input-fields-component';
import {OutOfStockComponent} from './components/out-of-stock/out-of-stock-component';
import {ProductOverlayOptions} from '../../../overlay';
import {ShopifyVariant} from '../../../interfaces/shopify-product.interface';

export class ShopifyHarryPotterProductInfo extends ShopifyProductInfo {
  private outOfStock: OutOfStockComponent;

  constructor(
    container: HTMLElement,
    item: SphereItem,
    protected planogram: Planogram,
    protected redirectToProduct: (productLink: string) => void,
    options: ProductOverlayOptions,
    productInfoService: ShopifyProductInfoService,
    protected shoppingCartService: ShoppingCartService,
    protected currencyService: CurrencyService,
    closeCallback: (changeUrl: boolean) => void
  ) {
    super(
      container,
      item,
      planogram,
      redirectToProduct,
      options,
      productInfoService,
      shoppingCartService,
      currencyService,
      closeCallback
    );
  }

  get isProductAvailable() {
    return this.variant?.inventory_quantity > 0;
  }

  protected async init(): Promise<void> {
    await super.init();

    if (this.product.input_fields.length && this.overlaySettings.enable_buttons) {
      this.inputFields = new InputFieldsComponent(this.container, this.product, this.shopifyService);
    }
  }

  protected setItemContent() {
    this.container.innerHTML = shopifyHarryPotterOverlayView(this.product, this.overlaySettings.enable_buttons);
    this.mainContainer = document.getElementById('shopify-product-details-container');
    if (this.overlaySettings.enable_buttons) {
      this.variantComponents = this.product.options.map(option => {
        return new VariantsComponent(option, this.product, this.mainContainer, this.shopifyService);
      });
      this.quantityPicker = new QuantityPicker(
        this.product,
        this.mainContainer,
        this.shopifyService,
        this.shoppingCartService
      );
      this.outOfStock = new OutOfStockComponent(
        this.planogram,
        this.mainContainer,
        this.product,
        this.shopifyService,
        this.shoppingCartService
      );
      this.buttons = new ShopifyButtons(
        this,
        this.product,
        this.overlaySettings,
        this.mainContainer,
        this.quantityPicker,
        this.shopifyService,
        this.shoppingCartService
      );
      this.shoppingCartWidget = new ShoppingCartWidgetComponent(
        this.planogram,
        this.shoppingCartService,
        this.currencyService,
        this.redirectToProduct,
        this.quantityPicker,
        'app-ecommerce-cart-container',
        this.product
      );
    }
    this.productFields = new ProductFields(this.product, this.mainContainer, this.overlaySettings);
    this.setOverlayFonts();
    this.syncEcommerceOverlayColor();
  }

  protected updateProductPrice(selectedVariant: ShopifyVariant) {
    if (!this.mainContainer) {
      return;
    }
    const priceWrapper = this.mainContainer.querySelector<HTMLElement>('.app-ecommerce-product-price');
    const priceContainer = priceWrapper.querySelector<HTMLElement>('.app-ecommerce-product-price-value');
    const discountPriceContainer = priceWrapper.querySelector<HTMLElement>(
      '.app-ecommerce-product-discount-price-value'
    );
    priceWrapper.classList.remove('has-discount-price', 'is-unavailable', 'is-unavailable-without-margin');

    if (!selectedVariant && !selectedVariant.isAutoSelected) {
      priceWrapper.classList.add('is-unavailable-without-margin');
      (priceContainer as HTMLElement).innerText = '';
      (discountPriceContainer as HTMLElement).innerText = '';
      return;
    } else if (selectedVariant.inventory_quantity <= 0 && !selectedVariant.isAutoSelected) {
      priceWrapper?.classList.add('is-unavailable');
    }

    const {selectedCurrencyCode, currenciesList} = this.currencyService;
    const getPriceValue = price =>
      CurrencyUtils.getPriceForHarryPotterWithSymbolOrCode(price, selectedCurrencyCode, currenciesList);

    if (selectedVariant?.compare_at_price && parseFloat(selectedVariant?.compare_at_price)) {
      // due to weird behaviour of shopify api discount price logic
      (priceContainer as HTMLElement).innerHTML = getPriceValue(selectedVariant.compare_at_price);
      (discountPriceContainer as HTMLElement).innerHTML = getPriceValue(selectedVariant.price);
      priceWrapper.classList.add('has-discount-price');
    } else {
      (priceContainer as HTMLElement).innerHTML = getPriceValue(selectedVariant.price);
      (discountPriceContainer as HTMLElement).innerHTML = '';
    }
  }

  dispose() {
    super.dispose();
    this.outOfStock?.dispose();
  }
}
